import { useState, useEffect, useCallback, useMemo } from 'react';
import logger from '../../../../utils/logging/logger';
import { computeDisplayTotalPrice } from './pricing';
import { useCreatePaymentIntent } from './useCreatePaymentIntent';

// Payment-intent lifecycle for usePaymentForm, extracted VERBATIM: the
// clientSecret/paymentIntentCreated/showPaymentForm/creatingPaymentIntent/
// processing state, the clientSecret<->parent sync effects, the
// recreate-on-discount-change effect, getTotalPrice/totalPrice, and
// handleProceedToPayment. createPaymentIntent itself is delegated to
// useCreatePaymentIntent (its own file, since the callback alone is ~500
// lines) so this file stays under the line-count cap.
export function usePaymentIntent({
  productIds,
  courses,
  coursesRef,
  appliedPromo,
  appliedPromoRef,
  bulkPricing,
  bulkPricingRef,
  productIdsRef,
  clientSecretFromParent,
  onClientSecretChange,
  stripeConfig,
  loading,
  error,
  setError,
  navigate,
  scrollToPaymentTop,
  paymentIntentCreatedRef,
  paymentIntentCreationLockRef,
  initialPaymentIntentAttemptedRef,
  lastPaymentIntentAttemptRef,
  apiCallTimestampsRef,
  processingRef,
  isRestoringBulkPricingRef,
  prevBulkPricingRef,
  prevAppliedPromoRef,
  RATE_LIMIT_DELAY,
}) {
  const [processing, setProcessing] = useState(false);
  const [clientSecret, setClientSecret] = useState(clientSecretFromParent || null);
  const [paymentIntentCreated, setPaymentIntentCreated] = useState(!!clientSecretFromParent);
  // Track if user has clicked "Proceed to Payment" - payment intent will be created on click
  const [showPaymentForm, setShowPaymentForm] = useState(!!clientSecretFromParent);
  const [creatingPaymentIntent, setCreatingPaymentIntent] = useState(false);

  const { createPaymentIntent } = useCreatePaymentIntent({
    productIds,
    appliedPromo,
    bulkPricing,
    onClientSecretChange,
    processing,
    error,
    stripeConfig,
    loading,
    courses,
    coursesRef,
    appliedPromoRef,
    bulkPricingRef,
    productIdsRef,
    clientSecret,
    setClientSecret,
    setProcessing,
    setError,
    setPaymentIntentCreated,
    paymentIntentCreatedRef,
    paymentIntentCreationLockRef,
    initialPaymentIntentAttemptedRef,
    lastPaymentIntentAttemptRef,
    apiCallTimestampsRef,
    RATE_LIMIT_DELAY,
    navigate,
  });

  // Sync clientSecret from parent (set after parent mounts Elements with options)
  // FIX: Only update if the value actually changed to prevent unnecessary re-renders
  useEffect(() => {
    if (clientSecretFromParent && clientSecretFromParent !== clientSecret) {
      setClientSecret(clientSecretFromParent);
      setPaymentIntentCreated(true);
      paymentIntentCreatedRef.current = true; // Update ref immediately
      initialPaymentIntentAttemptedRef.current = true; // Mark as attempted since parent provided it
    }
    // Only react when value changes from parent
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientSecretFromParent, clientSecret]);

  // Handle parent-provided clientSecret (for special flows)
  useEffect(() => {
    if (clientSecretFromParent && !clientSecret) {
      logger.info('Using clientSecret from parent', { hasClientSecretFromParent: !!clientSecretFromParent });
      setClientSecret(clientSecretFromParent);
      setPaymentIntentCreated(true);
      paymentIntentCreatedRef.current = true;
      setShowPaymentForm(true); // Show payment form if parent provided clientSecret
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientSecretFromParent]);

  // Show payment form when clientSecret becomes available (after "Proceed to Payment" click)
  useEffect(() => {
    if (clientSecret && paymentIntentCreatedRef.current && !showPaymentForm) {
      logger.info('ClientSecret available, showing payment form');
      setShowPaymentForm(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientSecret]);

  // FIX #9: Memoize total price calculation to prevent unnecessary recalculations
  const getTotalPrice = useCallback(
    () => computeDisplayTotalPrice({ courses, bulkPricing, appliedPromo, productIds }),
    [courses, bulkPricing, appliedPromo, productIds]
  );

  // FIX #9: Memoize total price for display to prevent recalculations on every render
  // getTotalPrice already depends on courses, so we only need getTotalPrice in the dependency array
  const totalPrice = useMemo(() => {
    const price = getTotalPrice();
    console.log('[FREE_ENROLLMENT] totalPrice memoized value:', {
      price,
      isZero: price === 0
    });
    return price;
  }, [getTotalPrice]);

  // Handler for "Proceed to Payment" button
  const handleProceedToPayment = async () => {
    console.log('[FREE_ENROLLMENT] handleProceedToPayment called', {
      creatingPaymentIntent,
      paymentIntentCreated: paymentIntentCreatedRef.current,
      showPaymentForm
    });

    if (creatingPaymentIntent || paymentIntentCreatedRef.current) {
      console.log('[FREE_ENROLLMENT] Already creating or created, returning early');
      return; // Already creating or created
    }

    // Check if total is $0 (free enrollment) - backend will handle it in createPaymentIntent
    // We still call createPaymentIntent, but the backend will skip Stripe and return enrollment data
    const currentTotal = getTotalPrice();
    console.log('[FREE_ENROLLMENT] Total calculated:', {
      currentTotal,
      isZero: currentTotal === 0,
      isLessThanOrEqualZero: currentTotal <= 0,
      appliedPromo: appliedPromo?.code,
      promoDiscount: appliedPromo ? `${appliedPromo.discount_type === 'percentage' ? appliedPromo.discount_value + '%' : '$' + appliedPromo.discount_value}` : null
    });

    // For $0 totals, we still need to call the backend to create enrollments
    // The backend will detect $0 and skip Stripe payment intent creation
    // This is intentional - we need the backend to handle enrollment creation, transaction records, and emails
    if (currentTotal === 0 || currentTotal <= 0) {
      console.log('[FREE_ENROLLMENT] ✅ Total is $0 - calling backend for free enrollment (no Stripe payment intent will be created)');
      logger.info('Total is $0 - calling backend for free enrollment');
    }

    setCreatingPaymentIntent(true);
    setError(null);

    try {
      console.log('[FREE_ENROLLMENT] Calling backend API (will skip Stripe if $0):', {
        currentTotal,
        isZero: currentTotal === 0,
        courses: courses?.map(c => ({ id: c.id, name: c.name, price: c.price })),
        courseCount: courses?.length,
        note: currentTotal === 0 ? 'Backend will detect $0 and skip Stripe, creating enrollments directly' : 'Backend will create Stripe payment intent'
      });
      logger.info('User clicked button - calling backend API', {
        totalAmount: currentTotal,
        isFreeEnrollment: currentTotal === 0
      });
      const paymentIntentResult = await createPaymentIntent(true); // Backend handles both paid and free enrollments
      if (paymentIntentResult === false) {
        logger.warn('Payment intent creation failed; preserving detailed error message');
        return;
      }

      // For free enrollments, createPaymentIntent will navigate away, so this code won't run
      // For paid enrollments, check if payment intent was created
      // Wait a moment for state to update
      console.log('[FREE_ENROLLMENT] Waiting for state update after createPaymentIntent call...');
      await new Promise(resolve => setTimeout(resolve, 300));

      console.log('[FREE_ENROLLMENT] After createPaymentIntent - checking state:', {
        paymentIntentCreated: paymentIntentCreatedRef.current,
        hasClientSecret: !!clientSecret,
        currentTotal,
        showPaymentForm
      });

      // Check if this was a free enrollment (navigation would have happened)
      // If paymentIntentCreatedRef is still false and we have no clientSecret, it might be a free enrollment
      // or an error - check if we're still on this page
      if (!paymentIntentCreatedRef.current && !clientSecret && currentTotal === 0) {
        // This might be a free enrollment that didn't navigate - check response
        console.warn('[FREE_ENROLLMENT] ⚠️ Free enrollment may not have completed - checking state', {
          paymentIntentCreated: paymentIntentCreatedRef.current,
          hasClientSecret: !!clientSecret,
          currentTotal
        });
        logger.warn('Free enrollment may not have completed - checking state');
        // The navigation should have happened in createPaymentIntent, but if it didn't,
        // we'll let the user know there was an issue
        setError('Enrollment may have completed. Please check your courses or try again.');
      } else if (paymentIntentCreatedRef.current) {
        // Payment intent was created successfully - show the form
        console.log('[FREE_ENROLLMENT] Payment intent created successfully - showing payment form');
        setShowPaymentForm(true);
        requestAnimationFrame(scrollToPaymentTop);
        logger.info('Payment intent created, showing payment form');
      } else if (currentTotal > 0) {
        // For paid enrollments, if payment intent wasn't created, show error
        console.error('[FREE_ENROLLMENT] ❌ Payment intent not created for paid enrollment');
        setError('Failed to initialize payment. Please try again.');
        logger.error('Payment intent not created after Proceed to Payment click');
      }
    } catch (error) {
      console.error('[FREE_ENROLLMENT] ❌ Error in handleProceedToPayment catch block:', {
        errorMessage: error.message,
        currentTotal,
        error
      });
      logger.error('Error creating payment intent on Proceed to Payment', { error });
      // Only show error if it's not a navigation (which would unmount the component)
      if (currentTotal > 0) {
        setError('Failed to initialize payment. Please try again.');
      }
    } finally {
      console.log('[FREE_ENROLLMENT] handleProceedToPayment finally block - resetting creatingPaymentIntent');
      setCreatingPaymentIntent(false);
    }
  };

  // Recreate payment intent when ANY discount changes (promo codes, bulk pricing, etc.)
  useEffect(() => {
    // CRITICAL: Skip if we're restoring bulk pricing to prevent loops
    if (isRestoringBulkPricingRef.current) {
      logger.debug('Skipping discount change check - restoring bulk pricing');
      return;
    }

    // FIX: Use ref to check current value to avoid dependency issues
    // Don't run if payment intent hasn't been created yet (initial creation is handled by another useEffect)
    if (!paymentIntentCreatedRef.current) {
      // Update refs for initial state tracking - only if they're different to prevent false change detection
      if (prevBulkPricingRef.current?.pricing?.finalPrice !== bulkPricing?.pricing?.finalPrice) {
        prevBulkPricingRef.current = bulkPricing;
      }
      if (prevAppliedPromoRef.current?.code !== appliedPromo?.code) {
        prevAppliedPromoRef.current = appliedPromo;
      }
      logger.debug('Discount change useEffect: Payment intent not created yet, skipping', {
        hasAppliedPromo: !!appliedPromo,
        promoCode: appliedPromo?.code,
        hasBulkPricing: !!bulkPricing
      });
      return;
    }

    // FIX #19: Extract stable values for comparison (use current values, not refs)
    const currentBulkPricingFinalPrice = bulkPricing?.pricing?.finalPrice;
    const currentBulkPricingDiscount = bulkPricing?.pricing?.discountAmount;
    const currentPromoCode = appliedPromo?.code;
    const currentPromoDiscount = appliedPromo?.discount_value;

    // Check if values actually changed (compare with previous ref values)
    const bulkPricingChanged =
      (prevBulkPricingRef.current?.pricing?.finalPrice !== currentBulkPricingFinalPrice) ||
      (prevBulkPricingRef.current?.pricing?.discountAmount !== currentBulkPricingDiscount) ||
      (!!prevBulkPricingRef.current !== !!bulkPricing);

    const promoChanged =
      (prevAppliedPromoRef.current?.code !== currentPromoCode) ||
      (prevAppliedPromoRef.current?.discount_value !== currentPromoDiscount) ||
      (!!prevAppliedPromoRef.current !== !!appliedPromo);

    // Debug logging
    logger.debug('Discount change useEffect triggered', {
      promoChanged,
      bulkPricingChanged,
      currentPromoCode,
      previousPromoCode: prevAppliedPromoRef.current?.code,
      hasAppliedPromo: !!appliedPromo,
      paymentIntentCreated: paymentIntentCreatedRef.current
    });

    // FIX #19: Update refs atomically AFTER comparison but BEFORE any async operations
    // CRITICAL: Only update refs if there was an actual change to prevent false change detection
    // This prevents the ref from being updated when bulk pricing is just being restored or when
    // the useEffect runs due to other dependency changes (like clientSecret)
    if (bulkPricingChanged) {
      prevBulkPricingRef.current = bulkPricing;
    }
    if (promoChanged) {
      prevAppliedPromoRef.current = appliedPromo;
    }

    // Only recreate if we have courses, payment intent is already created, AND there are actual discount changes
    // Also check that we're not already processing to prevent infinite loops
    // CRITICAL: Also check that we have a clientSecret (payment intent exists) before recreating
    // Only recreate if there's actually a discount applied (bulk pricing or promo)
    if (courses && Array.isArray(courses) && courses.length > 0 &&
        paymentIntentCreatedRef.current &&
        clientSecret && // CRITICAL: Only recreate if we already have a payment intent
        !processing &&
        (bulkPricingChanged || promoChanged)) {
      // FIX #5: Use single source of truth - getTotalPrice() function
      const totalAmount = getTotalPrice();

      logger.debug('Discount change detected, recreating payment intent', {
        appliedPromo: appliedPromo ? appliedPromo.code : null,
        bulkPricing: bulkPricing ? bulkPricing.pricing : null,
        totalAmount,
        bulkPricingChanged,
        promoChanged
      });
      // FIX #18: Increased debounce to prevent rapid-fire calls (was 500ms, now 1000ms)
      // FIX #1: Use refs to avoid stale closures - check current values at timeout execution
      // FIX: Ensure state is fully updated before recreating payment intent
      const timeoutId = setTimeout(() => {
        // Double-check we're not processing and payment intent exists before recreating
        // Use refs to get current values (not stale closure values)
        if (!processingRef.current && paymentIntentCreatedRef.current) {
          // FIX: Use refs to get current state values (avoid stale closures in setTimeout)
          const currentAppliedPromo = appliedPromoRef.current;
          const currentBulkPricing = bulkPricingRef.current;

          // Verify that the discount state is still valid before recreating
          // This ensures we don't recreate with stale state
          logger.debug('Recreating payment intent after discount change', {
            hasAppliedPromo: !!currentAppliedPromo,
            hasBulkPricing: !!currentBulkPricing,
            promoCode: currentAppliedPromo?.code,
            // Calculate total with current state values
            totalAmount: (() => {
              // FIX: Add null safety check for courses array
              const baseTotal = (courses && Array.isArray(courses) && courses.length > 0) ? courses.reduce((sum, course) => {
                if (!course) return sum;
                const price = (course.isPaidCourse || course.is_paid_course) ? (parseFloat(course.price) || 0) : 0;
                return Math.round((sum + price) * 100) / 100;
              }, 0) : 0;

              if (currentAppliedPromo) {
                if (currentAppliedPromo.discount_type === 'percentage') {
                  const discountValue = parseFloat(currentAppliedPromo.discount_value) || 0;
                  const discountAmount = baseTotal * (discountValue / 100);
                  return Math.max(0, Math.round((baseTotal - discountAmount) * 100) / 100);
                } else {
                  const discountValue = parseFloat(currentAppliedPromo.discount_value) || 0;
                  return Math.max(0, Math.round((baseTotal - discountValue) * 100) / 100);
                }
              } else if (currentBulkPricing && currentBulkPricing?.pricing?.finalPrice && !productIds) {
                return Math.round(parseFloat(currentBulkPricing.pricing.finalPrice) * 100) / 100;
              }
              return baseTotal;
            })()
          });
          // FIX: Use forceRecreate flag but don't clear clientSecret to prevent remount loop
          // The payment intent will be updated with the new amount
          createPaymentIntent(true);
        }
      }, 1000); // 1000ms debounce to prevent race conditions and ensure state is updated

      // FIX #7 & #19: Always return cleanup function to prevent memory leaks
      return () => {
        clearTimeout(timeoutId);
      };
    }
    // FIX #7 & #19: Return empty cleanup function if condition is false to maintain consistent hook behavior
    // This ensures cleanup is always called, preventing memory leaks
    return () => {
      // No timeout to clean up, but function exists for consistency
    };
    // FIX: Remove createPaymentIntent and getTotalPrice from dependencies to prevent infinite loop
    // These functions are memoized and stable, but including them causes loops when they're recreated
    // We use refs to access current values in setTimeout callbacks, so stale closures aren't an issue
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appliedPromo, bulkPricing, courses.length, processing]); // Removed createPaymentIntent and getTotalPrice to prevent loops

  return {
    clientSecret,
    setClientSecret,
    paymentIntentCreated,
    setPaymentIntentCreated,
    showPaymentForm,
    setShowPaymentForm,
    creatingPaymentIntent,
    processing,
    setProcessing,
    createPaymentIntent,
    handleProceedToPayment,
    totalPrice,
  };
}
